import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, BookOpen, Gamepad2, Plus, Trash2, Pencil, Save, X, Eye, EyeOff,
  ListChecks, ArrowLeft, CheckCircle2,
} from "lucide-react";

const TABS = [
  { id: "guides",  label: "Guides",   icon: BookOpen },
  { id: "quizzes", label: "Quizzes",  icon: Gamepad2 },
];

export default function AdminAcademy() {
  const [tab, setTab] = useState("guides");
  return (
    <div data-testid="admin-academy-page">
      <h1 className="font-display text-2xl sm:text-3xl font-semibold">Guides & Games CMS</h1>
      <p className="text-sm text-slate-600 mt-1">
        Create, edit and publish guides and quizzes. Each item is locked to one audience -
        renter content shows at <code className="px-1 bg-slate-100 rounded">/academy</code>,
        operator content at <code className="px-1 bg-slate-100 rounded">/businesses/academy</code>.
      </p>

      <div className="mt-6 flex gap-1.5 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.id}
                   data-testid={`admin-academy-tab-${t.id}`}
                   onClick={() => setTab(t.id)}
                   className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition ${
                     tab === t.id
                       ? "border-emerald-600 text-emerald-700"
                       : "border-transparent text-slate-500 hover:text-slate-800"
                   }`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "guides"  && <GuidesPanel />}
        {tab === "quizzes" && <QuizzesPanel />}
      </div>
    </div>
  );
}

/* ---------------------------------- Guides --------------------------------- */

function GuidesPanel() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | guide doc
  const [err, setErr] = useState(null);

  function refresh() {
    api.get("/admin/academy/guides")
      .then(({ data }) => setItems(data.items))
      .catch((e) => setErr(formatApiError(e)));
  }
  useEffect(refresh, []);

  if (editing) {
    return <GuideEditor guide={editing === "new" ? null : editing}
                          onSaved={() => { refresh(); setEditing(null); }}
                          onCancel={() => setEditing(null)} />;
  }

  return (
    <div data-testid="admin-guides-panel">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">{items ? `${items.length} guide(s)` : "Loading…"}</div>
        <button data-testid="admin-guides-new"
                onClick={() => setEditing("new")}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#1B4332] text-white text-sm font-medium hover:bg-[#143027]">
          <Plus className="w-4 h-4" /> New guide
        </button>
      </div>
      {err && <div className="mt-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 p-3 text-sm">{err}</div>}
      {!items ? <Loading /> :
        items.length === 0 ? <Empty message="No guides yet. Click 'New guide' to create one." /> :
        <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-[0.16em] text-slate-500">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Title</th>
                <th className="text-left px-3 py-2 font-semibold">Slug</th>
                <th className="text-left px-3 py-2 font-semibold">Audience</th>
                <th className="text-left px-3 py-2 font-semibold">Status</th>
                <th className="text-right px-3 py-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((g) => (
                <tr key={g.id} className="border-t border-slate-100"
                     data-testid={`admin-guide-row-${g.slug}`}>
                  <td className="px-3 py-2 font-medium">{g.title}</td>
                  <td className="px-3 py-2 text-slate-500">{g.slug}</td>
                  <td className="px-3 py-2"><AudiencePill a={g.audience} /></td>
                  <td className="px-3 py-2">
                    {g.is_published
                      ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium"><Eye className="w-3 h-3" /> Published</span>
                      : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-medium"><EyeOff className="w-3 h-3" /> Hidden</span>}
                    {g.is_featured && <span className="ml-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-medium">Featured</span>}
                  </td>
                  <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                    <button data-testid={`admin-guide-edit-${g.slug}`}
                            onClick={() => setEditing(g)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 text-xs">
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button data-testid={`admin-guide-delete-${g.slug}`}
                            onClick={async () => {
                              if (!window.confirm(`Delete guide "${g.title}"?`)) return;
                              try {
                                await api.delete(`/admin/academy/guides/${g.id}`);
                                toast.success("Guide deleted.");
                                refresh();
                              } catch (e) { toast.error(formatApiError(e)); }
                            }}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-rose-200 text-rose-700 hover:bg-rose-50 text-xs">
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}
    </div>
  );
}

function GuideEditor({ guide, onSaved, onCancel }) {
  const isNew = !guide;
  const [form, setForm] = useState(guide || {
    title: "", slug: "", subtitle: "", audience: "renter", category: "",
    body_markdown: "", reading_time_minutes: 5, is_published: true, is_featured: false,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  function set(k, v) { setForm({ ...form, [k]: v }); }
  function autoSlug() {
    if (!form.title) return;
    const s = form.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 80);
    set("slug", s);
  }

  async function save() {
    setBusy(true); setErr(null);
    try {
      const payload = {
        title: form.title, slug: form.slug, subtitle: form.subtitle || null,
        audience: form.audience, category: form.category || null,
        body_markdown: form.body_markdown,
        reading_time_minutes: Number(form.reading_time_minutes) || 5,
        is_published: !!form.is_published, is_featured: !!form.is_featured,
      };
      if (isNew) await api.post("/admin/academy/guides", payload);
      else       await api.put(`/admin/academy/guides/${guide.id}`, payload);
      toast.success(isNew ? "Guide created." : "Guide updated.");
      onSaved();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  }

  return (
    <div data-testid="admin-guide-editor" className="space-y-5">
      <button onClick={onCancel} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-emerald-700">
        <ArrowLeft className="w-4 h-4" /> Back to guides
      </button>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-display text-xl font-semibold">{isNew ? "New guide" : `Edit: ${guide.title}`}</h2>
        <div className="flex gap-2">
          <button onClick={onCancel} className="px-3 py-2 rounded-lg border border-slate-200 text-sm hover:bg-slate-50"><X className="w-4 h-4 inline -mt-0.5" /> Cancel</button>
          <button data-testid="admin-guide-save"
                   disabled={busy || !form.title || !form.slug || !form.body_markdown}
                   onClick={save}
                   className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#1B4332] text-white text-sm font-medium hover:bg-[#143027] disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
        </div>
      </div>
      {err && <div className="rounded-lg bg-rose-50 border border-rose-200 text-rose-700 p-3 text-sm">{err}</div>}

      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Title">
          <input data-testid="guide-input-title" value={form.title}
                  onChange={(e) => set("title", e.target.value)} onBlur={() => { if (isNew && !form.slug) autoSlug(); }}
                  className="input" />
        </Field>
        <Field label="Slug (URL)">
          <input data-testid="guide-input-slug" value={form.slug}
                  onChange={(e) => set("slug", e.target.value.toLowerCase())}
                  pattern="[a-z0-9-]+" className="input" />
        </Field>
        <Field label="Subtitle">
          <input data-testid="guide-input-subtitle" value={form.subtitle || ""}
                  onChange={(e) => set("subtitle", e.target.value)} className="input" />
        </Field>
        <Field label="Category">
          <input data-testid="guide-input-category" value={form.category || ""}
                  onChange={(e) => set("category", e.target.value)} className="input" />
        </Field>
        <Field label="Where will this appear?">
          <select data-testid="guide-input-audience" value={form.audience === "both" ? "renter" : form.audience}
                   onChange={(e) => set("audience", e.target.value)} className="input">
            <option value="renter">Public site (/academy), for renters</option>
            <option value="business">Business site (/businesses/academy), for operators</option>
          </select>
        </Field>
        <Field label="Reading time (min)">
          <input type="number" data-testid="guide-input-reading-time"
                  value={form.reading_time_minutes}
                  onChange={(e) => set("reading_time_minutes", e.target.value)} className="input" />
        </Field>
        <div className="flex items-center gap-4 sm:col-span-2">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="guide-input-published"
                    checked={!!form.is_published}
                    onChange={(e) => set("is_published", e.target.checked)} />
            Published
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="guide-input-featured"
                    checked={!!form.is_featured}
                    onChange={(e) => set("is_featured", e.target.checked)} />
            Featured
          </label>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1.5">Markdown</div>
          <textarea data-testid="guide-input-body"
                    value={form.body_markdown}
                    onChange={(e) => set("body_markdown", e.target.value)}
                    rows={28}
                    className="input font-mono text-xs leading-relaxed"
                    placeholder="## Your headline&#10;Body content…"/>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1.5">Live preview</div>
          <div className="rounded-xl border border-slate-200 bg-white p-5 h-[680px] overflow-y-auto prose prose-slate prose-emerald max-w-none"
                data-testid="guide-preview">
            <ReactMarkdown>{form.body_markdown || "*Start typing markdown on the left.*"}</ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- Quizzes --------------------------------- */

function QuizzesPanel() {
  const [items, setItems] = useState(null);
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [manageQuestionsFor, setManageQuestionsFor] = useState(null);
  const [err, setErr] = useState(null);

  function refresh() {
    api.get("/admin/academy/quizzes")
      .then(({ data }) => setItems(data.items))
      .catch((e) => setErr(formatApiError(e)));
  }
  useEffect(refresh, []);

  if (manageQuestionsFor) {
    return <QuestionsManager quiz={manageQuestionsFor} onBack={() => { refresh(); setManageQuestionsFor(null); }} />;
  }
  if (editingQuiz) {
    return <QuizEditor quiz={editingQuiz === "new" ? null : editingQuiz}
                        onSaved={() => { refresh(); setEditingQuiz(null); }}
                        onCancel={() => setEditingQuiz(null)} />;
  }

  return (
    <div data-testid="admin-quizzes-panel">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">{items ? `${items.length} quiz(zes)` : "Loading…"}</div>
        <button data-testid="admin-quizzes-new"
                onClick={() => setEditingQuiz("new")}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#1B4332] text-white text-sm font-medium hover:bg-[#143027]">
          <Plus className="w-4 h-4" /> New quiz
        </button>
      </div>
      {err && <div className="mt-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 p-3 text-sm">{err}</div>}
      {!items ? <Loading /> :
        items.length === 0 ? <Empty message="No quizzes yet. Click 'New quiz' to create one." /> :
        <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-[0.16em] text-slate-500">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Title</th>
                <th className="text-left px-3 py-2 font-semibold">Slug</th>
                <th className="text-left px-3 py-2 font-semibold">Audience</th>
                <th className="text-left px-3 py-2 font-semibold">Questions</th>
                <th className="text-left px-3 py-2 font-semibold">Status</th>
                <th className="text-right px-3 py-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((q) => (
                <tr key={q.id} className="border-t border-slate-100"
                     data-testid={`admin-quiz-row-${q.slug}`}>
                  <td className="px-3 py-2 font-medium">{q.title}</td>
                  <td className="px-3 py-2 text-slate-500">{q.slug}</td>
                  <td className="px-3 py-2"><AudiencePill a={q.audience} /></td>
                  <td className="px-3 py-2">{q.active_questions || 0} active / {q.total_questions || 0} total</td>
                  <td className="px-3 py-2">
                    {q.is_active
                      ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium"><Eye className="w-3 h-3" /> Active</span>
                      : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-medium"><EyeOff className="w-3 h-3" /> Hidden</span>}
                  </td>
                  <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                    <button data-testid={`admin-quiz-manage-questions-${q.slug}`}
                            onClick={() => setManageQuestionsFor(q)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 text-xs">
                      <ListChecks className="w-3.5 h-3.5" /> Questions
                    </button>
                    <button data-testid={`admin-quiz-edit-${q.slug}`}
                            onClick={() => setEditingQuiz(q)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 text-xs">
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button data-testid={`admin-quiz-delete-${q.slug}`}
                            onClick={async () => {
                              if (!window.confirm(`Delete quiz "${q.title}" and ALL its questions?`)) return;
                              try {
                                await api.delete(`/admin/academy/quizzes/${q.id}`);
                                toast.success("Quiz deleted.");
                                refresh();
                              } catch (e) { toast.error(formatApiError(e)); }
                            }}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-rose-200 text-rose-700 hover:bg-rose-50 text-xs">
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}
    </div>
  );
}

function QuizEditor({ quiz, onSaved, onCancel }) {
  const isNew = !quiz;
  const [form, setForm] = useState(quiz || {
    title: "", slug: "", description: "", audience: "renter", difficulty: "practical",
    questions_per_session: 10, total_question_target: 50, is_active: true,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  function set(k, v) { setForm({ ...form, [k]: v }); }

  async function save() {
    setBusy(true); setErr(null);
    try {
      const payload = {
        title: form.title, slug: form.slug, description: form.description || null,
        audience: form.audience, difficulty: form.difficulty,
        questions_per_session: Number(form.questions_per_session) || 10,
        total_question_target: Number(form.total_question_target) || 50,
        is_active: !!form.is_active,
      };
      if (isNew) await api.post("/admin/academy/quizzes", payload);
      else       await api.put(`/admin/academy/quizzes/${quiz.id}`, payload);
      toast.success(isNew ? "Quiz created." : "Quiz updated.");
      onSaved();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  }

  return (
    <div data-testid="admin-quiz-editor" className="space-y-5">
      <button onClick={onCancel} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-emerald-700">
        <ArrowLeft className="w-4 h-4" /> Back to quizzes
      </button>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-display text-xl font-semibold">{isNew ? "New quiz" : `Edit: ${quiz.title}`}</h2>
        <button data-testid="admin-quiz-save"
                 disabled={busy || !form.title || !form.slug}
                 onClick={save}
                 className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#1B4332] text-white text-sm font-medium hover:bg-[#143027] disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save quiz
        </button>
      </div>
      {err && <div className="rounded-lg bg-rose-50 border border-rose-200 text-rose-700 p-3 text-sm">{err}</div>}

      <div className="grid sm:grid-cols-2 gap-3 max-w-3xl">
        <Field label="Title"><input data-testid="quiz-input-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="input" /></Field>
        <Field label="Slug (URL)"><input data-testid="quiz-input-slug" value={form.slug} onChange={(e) => set("slug", e.target.value.toLowerCase())} className="input" /></Field>
        <Field label="Description" className="sm:col-span-2">
          <textarea data-testid="quiz-input-description" value={form.description || ""} onChange={(e) => set("description", e.target.value)} rows={3} className="input" />
        </Field>
        <Field label="Where will this appear?">
          <select data-testid="quiz-input-audience" value={form.audience === "both" ? "renter" : form.audience}
                   onChange={(e) => set("audience", e.target.value)} className="input">
            <option value="renter">Public site (/academy), for renters</option>
            <option value="business">Business site (/businesses/academy), for operators</option>
          </select>
        </Field>
        <Field label="Difficulty">
          <select data-testid="quiz-input-difficulty" value={form.difficulty} onChange={(e) => set("difficulty", e.target.value)} className="input">
            <option value="beginner">Beginner</option><option value="practical">Practical</option><option value="advanced">Advanced</option>
          </select>
        </Field>
        <Field label="Questions per session">
          <input type="number" data-testid="quiz-input-per-session" value={form.questions_per_session} onChange={(e) => set("questions_per_session", e.target.value)} className="input" />
        </Field>
        <Field label="Total question target">
          <input type="number" data-testid="quiz-input-total-target" value={form.total_question_target} onChange={(e) => set("total_question_target", e.target.value)} className="input" />
        </Field>
        <div className="sm:col-span-2">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="quiz-input-active" checked={!!form.is_active} onChange={(e) => set("is_active", e.target.checked)} />
            Active (visible on /academy)
          </label>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- Questions ------------------------------- */

function QuestionsManager({ quiz, onBack }) {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | question doc
  const [err, setErr] = useState(null);

  function refresh() {
    api.get(`/admin/academy/questions?quiz_id=${quiz.id}`)
      .then(({ data }) => setItems(data.items))
      .catch((e) => setErr(formatApiError(e)));
  }
  useEffect(refresh, [quiz.id]);

  if (editing) {
    return <QuestionEditor quiz={quiz}
                            question={editing === "new" ? null : editing}
                            onSaved={() => { refresh(); setEditing(null); }}
                            onCancel={() => setEditing(null)} />;
  }

  return (
    <div data-testid="admin-questions-manager" className="space-y-4">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-emerald-700">
        <ArrowLeft className="w-4 h-4" /> Back to quizzes
      </button>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-semibold">{quiz.title}</h2>
          <p className="text-sm text-slate-600">Question bank: target {quiz.total_question_target || 50}, players see {quiz.questions_per_session || 10} per round.</p>
        </div>
        <button data-testid="admin-question-new"
                onClick={() => setEditing("new")}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#1B4332] text-white text-sm font-medium hover:bg-[#143027]">
          <Plus className="w-4 h-4" /> New question
        </button>
      </div>
      {err && <div className="rounded-lg bg-rose-50 border border-rose-200 text-rose-700 p-3 text-sm">{err}</div>}
      {!items ? <Loading /> :
        items.length === 0 ? <Empty message="No questions yet. Add your first one." /> :
        <div className="space-y-2">
          {items.map((q, idx) => (
            <div key={q.id} className="rounded-xl border border-slate-200 bg-white p-4"
                  data-testid={`admin-question-row-${q.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-500">Q{idx + 1} · {q.type} · {q.difficulty}{q.category ? ` · ${q.category}` : ""}</div>
                  {q.scenario_text && <div className="text-xs text-slate-600 mt-1.5 italic line-clamp-2">{q.scenario_text}</div>}
                  <div className="font-medium mt-1.5 line-clamp-2">{q.question_text}</div>
                  <div className="text-xs text-emerald-700 mt-2 inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Correct: {String.fromCharCode(65 + q.correct_answer_index)}: {q.answer_options[q.correct_answer_index]}
                  </div>
                  {!q.is_active && <span className="ml-2 inline-flex px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-medium">Hidden</span>}
                </div>
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  <button data-testid={`admin-question-edit-${q.id}`}
                          onClick={() => setEditing(q)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 text-xs">
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button data-testid={`admin-question-delete-${q.id}`}
                          onClick={async () => {
                            if (!window.confirm("Delete this question?")) return;
                            try {
                              await api.delete(`/admin/academy/questions/${q.id}`);
                              toast.success("Question deleted.");
                              refresh();
                            } catch (e) { toast.error(formatApiError(e)); }
                          }}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-rose-200 text-rose-700 hover:bg-rose-50 text-xs">
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>}
    </div>
  );
}

function QuestionEditor({ quiz, question, onSaved, onCancel }) {
  const isNew = !question;
  const [form, setForm] = useState(question || {
    type: "best_decision", scenario_text: "", question_text: "",
    answer_options: ["", "", "", ""], correct_answer_index: 0,
    explanation: "", category: "", difficulty: "practical", is_active: true,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  function set(k, v) { setForm({ ...form, [k]: v }); }
  function setOpt(i, v) {
    const next = [...form.answer_options]; next[i] = v;
    setForm({ ...form, answer_options: next });
  }
  function addOpt() { if (form.answer_options.length >= 8) return; setForm({ ...form, answer_options: [...form.answer_options, ""] }); }
  function removeOpt(i) {
    if (form.answer_options.length <= 2) return;
    const next = form.answer_options.filter((_, idx) => idx !== i);
    let cidx = form.correct_answer_index;
    if (cidx >= next.length) cidx = next.length - 1;
    setForm({ ...form, answer_options: next, correct_answer_index: cidx });
  }

  async function save() {
    setBusy(true); setErr(null);
    try {
      const cleaned = form.answer_options.map((o) => o.trim()).filter(Boolean);
      if (cleaned.length < 2) throw new Error("At least 2 answer options.");
      if (form.correct_answer_index >= cleaned.length) throw new Error("Correct answer index is out of range.");
      const payload = {
        quiz_id: quiz.id, type: form.type,
        scenario_text: form.scenario_text || null,
        question_text: form.question_text,
        answer_options: cleaned,
        correct_answer_index: Number(form.correct_answer_index),
        explanation: form.explanation,
        category: form.category || null,
        difficulty: form.difficulty,
        is_active: !!form.is_active,
      };
      if (isNew) await api.post("/admin/academy/questions", payload);
      else {
        const { quiz_id: _omit, ...patch } = payload;
        await api.put(`/admin/academy/questions/${question.id}`, patch);
      }
      toast.success(isNew ? "Question created." : "Question updated.");
      onSaved();
    } catch (e) { setErr(formatApiError(e) || e.message); }
    finally { setBusy(false); }
  }

  return (
    <div data-testid="admin-question-editor" className="space-y-5">
      <button onClick={onCancel} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-emerald-700">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-display text-xl font-semibold">{isNew ? "New question" : "Edit question"}</h2>
        <button data-testid="admin-question-save"
                 disabled={busy || !form.question_text || !form.explanation}
                 onClick={save}
                 className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#1B4332] text-white text-sm font-medium hover:bg-[#143027] disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </button>
      </div>
      {err && <div className="rounded-lg bg-rose-50 border border-rose-200 text-rose-700 p-3 text-sm">{err}</div>}

      <div className="grid sm:grid-cols-3 gap-3 max-w-4xl">
        <Field label="Type">
          <select data-testid="q-input-type" value={form.type} onChange={(e) => set("type", e.target.value)} className="input">
            <option value="multiple_choice">Multiple choice</option>
            <option value="best_decision">Best decision</option>
            <option value="red_flag">Red flag</option>
            <option value="next_step">Next step</option>
            <option value="scenario">Scenario</option>
          </select>
        </Field>
        <Field label="Difficulty">
          <select data-testid="q-input-difficulty" value={form.difficulty} onChange={(e) => set("difficulty", e.target.value)} className="input">
            <option value="beginner">Beginner</option><option value="practical">Practical</option><option value="advanced">Advanced</option>
          </select>
        </Field>
        <Field label="Category"><input data-testid="q-input-category" value={form.category || ""} onChange={(e) => set("category", e.target.value)} className="input" /></Field>
      </div>

      <Field label="Scenario (optional: sets the stage)">
        <textarea data-testid="q-input-scenario" rows={3} value={form.scenario_text || ""} onChange={(e) => set("scenario_text", e.target.value)} className="input" />
      </Field>
      <Field label="Question">
        <textarea data-testid="q-input-question" rows={2} value={form.question_text} onChange={(e) => set("question_text", e.target.value)} className="input" />
      </Field>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">Answer options · pick correct</div>
          <button onClick={addOpt} disabled={form.answer_options.length >= 8}
                   className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 text-xs disabled:opacity-50">
            <Plus className="w-3.5 h-3.5" /> Add option
          </button>
        </div>
        <div className="space-y-2">
          {form.answer_options.map((opt, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`q-option-row-${i}`}>
              <input type="radio" data-testid={`q-option-correct-${i}`}
                      checked={form.correct_answer_index === i}
                      onChange={() => set("correct_answer_index", i)} />
              <span className="w-5 text-xs font-semibold text-slate-500">{String.fromCharCode(65 + i)}</span>
              <input data-testid={`q-option-text-${i}`} value={opt}
                      onChange={(e) => setOpt(i, e.target.value)} className="input flex-1" placeholder="Answer text…" />
              <button onClick={() => removeOpt(i)} disabled={form.answer_options.length <= 2}
                       className="p-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50 disabled:opacity-30">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <Field label="Explanation (shown after answering)">
        <textarea data-testid="q-input-explanation" rows={4} value={form.explanation} onChange={(e) => set("explanation", e.target.value)} className="input" />
      </Field>

      <label className="inline-flex items-center gap-2 text-sm">
        <input type="checkbox" data-testid="q-input-active" checked={!!form.is_active} onChange={(e) => set("is_active", e.target.checked)} />
        Active (included in random pool)
      </label>
    </div>
  );
}

/* --------------------------------- Helpers --------------------------------- */

function Field({ label, children, className }) {
  return (
    <label className={`block ${className || ""}`}>
      <div className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function AudiencePill({ a }) {
  if (a === "renter")   return <span className="px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 text-xs font-medium">Renters</span>;
  if (a === "business") return <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-medium">Operators</span>;
  return <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">Everyone</span>;
}

function Loading() { return <div className="flex items-center gap-2 text-slate-500 text-sm mt-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>; }
function Empty({ message }) { return <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500 text-sm">{message}</div>; }
